import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ExternalLink, ArrowUpRight, ArrowDownRight, Loader, AlertCircle } from 'lucide-react';
import { Token } from '../types/supabase';
import TokenSearchModal from './TokenSearchModal';
import { wsService } from '../services/websocketService';

// Define the trade type
interface Trade {
  tx: string;
  amount: number;
  priceUsd: number;
  volume: number;
  volumeSol?: number; // Optional SOL volume
  type: 'buy' | 'sell';
  wallet: string;
  time: number;
  program?: string; // Optional program field from WebSocket
}

interface OrderBookProps {
  selectedToken?: Token;
  isWidget?: boolean;
}

// Constants
const SOLANA_TRACKER_WS_URL = 'wss://data.solanatracker.io/ws';
const BUY_COLOR = '#00fe76'; // Bright green color from the buy button
const SELL_COLOR = '#ff5c5c'; // Bright red for sell
const MAX_WS_RETRY_ATTEMPTS = 3; // Maximum number of WebSocket retry attempts
const RETRY_DELAY_MS = 2000; // Initial retry delay in milliseconds

// Solana logo SVG as a component
const SolanaLogoUrl = "https://solana.com/src/img/branding/solanaLogoMark.svg";

// Add CSS for hiding scrollbars
const scrollbarHidingStyle = `
  .hide-scrollbar {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
  }
  
  .hide-scrollbar::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`;

// Add CSS for volume bar gradients
const volumeBarStyles = `
  .volume-bar-buy {
    background: linear-gradient(90deg, rgba(0, 254, 118, 0.05) 0%, rgba(0, 254, 118, 0.15) 100%);
    border-radius: 0 4px 4px 0;
  }
  .volume-bar-sell {
    background: linear-gradient(90deg, rgba(255, 92, 92, 0.05) 0%, rgba(255, 92, 92, 0.15) 100%);
    border-radius: 0 4px 4px 0;
  }
`;

const OrderBook: React.FC<OrderBookProps> = ({ selectedToken, isWidget = false }) => {
  const [localSelectedToken, setLocalSelectedToken] = useState<Token | undefined>(selectedToken);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isTokenSearchModalOpen, setIsTokenSearchModalOpen] = useState(false);
  const [searchButtonRef, setSearchButtonRef] = useState<HTMLButtonElement | null>(null);
  const [apiKeyMissing, setApiKeyMissing] = useState<boolean>(false);
  const [showSolValues, setShowSolValues] = useState<boolean>(false);
  const [wsStatus, setWsStatus] = useState<'connected' | 'connecting' | 'disconnected' | 'error' | 'disabled'>('disconnected');
  
  // Store the unsubscribe function
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Use the effective token (either local state or prop)
  const effectiveToken = localSelectedToken || selectedToken;

  // Function to broadcast token selection to other components
  const broadcastTokenSelection = useCallback((token: Token) => {
    console.log(`Broadcasting token selection: ${token.symbol}`);
    const event = new CustomEvent('ekko:token_selected', { 
      detail: { token },
      bubbles: true
    });
    window.dispatchEvent(event);
  }, []);

  // When local token changes, broadcast it
  useEffect(() => {
    if (localSelectedToken) {
      broadcastTokenSelection(localSelectedToken);
    }
  }, [localSelectedToken, broadcastTokenSelection]);

  // Function to fetch trades for a token
  const fetchTrades = useCallback(async (tokenAddress: string) => {
    if (!tokenAddress) return;
    
    // Don't attempt to fetch if API key is missing
    if (apiKeyMissing) {
      setError('API key not configured. Please add VITE_SOLANA_TRACKER_API_KEY to your environment variables.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
      
      // Log info for debugging (will be removed in production)
      console.log(`Using API key: ${apiKey ? 'Key found' : 'Key missing'}`);
      console.log(`Fetching trades for token: ${tokenAddress}`);
      
      // Different auth methods to try
      const headers: HeadersInit = {};
      if (apiKey) {
        // Try with Bearer auth (most common)
        headers['Authorization'] = `Bearer ${apiKey}`;
        // Some APIs also use X-API-Key header
        headers['X-API-Key'] = apiKey;
      }
      
      const response = await fetch(`https://data.solanatracker.io/trades/${tokenAddress}`, { headers });
      
      if (!response.ok) {
        console.error(`API Response: Status ${response.status}, StatusText: ${response.statusText}`);
        throw new Error(`Failed to fetch trades: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Sort trades by time (most recent first)
      const sortedTrades = (data.trades || []).sort((a: Trade, b: Trade) => b.time - a.time);
      
      setTrades(sortedTrades);
    } catch (err) {
      console.error('Error fetching trades:', err);
      setError(`Failed to load trade data: ${err instanceof Error ? err.message : 'Unknown error'}. Please verify the API key in your environment variables.`);
    } finally {
      setIsLoading(false);
    }
  }, [apiKeyMissing]);

  // Check if API key is configured
  useEffect(() => {
    const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
    if (!apiKey) {
      setApiKeyMissing(true);
      setError('API key not configured. Please add VITE_SOLANA_TRACKER_API_KEY to your environment variables.');
    } else {
      setApiKeyMissing(false);
    }
  }, []);

  // Listen for token selection events from other components
  useEffect(() => {
    const handleTokenSelected = (event: CustomEvent<{ token: Token }>) => {
      const { token } = event.detail;
      console.log('Token selected event received:', token.symbol);
      setLocalSelectedToken(token);
    };
    
    // Add event listener
    window.addEventListener('ekko:token_selected', handleTokenSelected as EventListener);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('ekko:token_selected', handleTokenSelected as EventListener);
    };
  }, []);

  // Setup WebSocket connection when token changes
  useEffect(() => {
    // Only subscribe if we have a token and API key
    if (!effectiveToken?.mint || apiKeyMissing) return;
    
    const tokenAddress = effectiveToken.mint;
    
    // Check connection status and update local state
    const updateConnectionStatus = () => {
      const status = wsService.getConnectionStatus();
      setWsStatus(status);
    };

    // Initially update status
    updateConnectionStatus();

    console.log(`Setting up WebSocket connection for token: ${tokenAddress}`);

    // First, always fetch initial data via REST API
    fetchTrades(tokenAddress);

    // Check if token is in any of the active feeds before subscribing
    const isInActiveFeeds = () => {
      try {
        // Check localStorage for token feed membership
        const newTokens = JSON.parse(localStorage.getItem('ekko_new_tokens') || '[]');
        const trendingTokens = JSON.parse(localStorage.getItem('ekko_trending_tokens') || '[]');
        const migratedTokens = JSON.parse(localStorage.getItem('ekko_migrated_tokens') || '[]');
        
        // Check if token is in any of these lists
        return newTokens.includes(tokenAddress) || 
               trendingTokens.includes(tokenAddress) || 
               migratedTokens.includes(tokenAddress);
      } catch (error) {
        console.error('Error checking feed membership:', error);
        // If we can't check, default to allowing subscription for widgets
        return isWidget;
      }
    };

    // Then attempt to set up the WebSocket connection for real-time updates
    wsService.ensureConnection().then((connected) => {
      if (connected) {
        // Define transaction handler function
        const handleTransaction = (data: any) => {
          console.log('OrderBook received WebSocket transaction data:', data);
          
          // Handle both single transaction objects and arrays of transactions
          const transactions = Array.isArray(data) ? data : [data];
          
          // Only process data that has valid transaction information
          if (transactions.length > 0) {
            // Add new transactions to the list
            setTrades(prevTrades => {
              // Process each transaction
              const newTrades = transactions
                // Consider a transaction valid if it has minimal useful info
                .filter(tx => tx && (
                  tx.tx || 
                  (tx.wallet && tx.time) || 
                  (tx.amount || tx.volume)
                ))
                .map(tx => ({
                  tx: tx.tx || `unknown-${Date.now()}`, // Generate temporary ID if missing
                  amount: tx.amount || 0,
                  priceUsd: tx.priceUsd || tx.price || 0,
                  volume: tx.volume || 0,
                  volumeSol: tx.volumeSol,
                  type: tx.type || 'buy',
                  wallet: tx.wallet || 'unknown',
                  time: tx.time || Date.now(),
                  program: tx.program
                }));
              
              if (newTrades.length > 0) {
                console.log(`Adding ${newTrades.length} new transactions`);
                
                // Combine with existing trades
                const combinedTrades = [...newTrades, ...prevTrades];
                
                // Remove duplicate transactions by tx id
                const uniqueTrades = combinedTrades
                  .filter((trade, index, self) => 
                    index === self.findIndex(t => t.tx === trade.tx)
                  );
                
                // Sort by time (most recent first)
                uniqueTrades.sort((a, b) => b.time - a.time);
                
                // Limit to the most recent 100 trades
                return uniqueTrades.slice(0, 100);
              }
              
              return prevTrades;
            });
          }
        };
        
        // Only set up transaction subscriptions if token is in active feeds or this is a widget (which gets special treatment)
        if (isInActiveFeeds() || isWidget) {
          console.log(`Subscribing to transactions for token ${tokenAddress}`);
          unsubscribeRef.current = wsService.subscribeToTransactions(tokenAddress, handleTransaction);
          
          // Also subscribe to the general 'message' event to see all messages for debugging
          const handleAllMessages = (message: any) => {
            console.log('WebSocket general message received:', message);
          };
          
          // Store both unsubscribe functions
          const unsubscribeMessages = wsService.on('message', handleAllMessages);
          const originalUnsubscribe = unsubscribeRef.current;
          
          unsubscribeRef.current = () => {
            originalUnsubscribe();
            unsubscribeMessages();
          };
        } else {
          console.log(`Token ${tokenAddress} is not in active feeds, skipping WebSocket subscription`);
          setWsStatus('disabled');
        }
      } else {
        // Fall back to REST API if WebSocket connection fails
        console.log('WebSocket connection failed, falling back to REST API');
        setWsStatus('disabled');
      }
    });
    
    // Cleanup subscription and disconnect WebSocket when component unmounts or token changes
    return () => {
      // Clean up subscription
      if (unsubscribeRef.current) {
        console.log('Cleaning up WebSocket subscription');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      // Leave the room for this token
      if (effectiveToken?.mint) {
        wsService.leaveRoom(`transaction:${effectiveToken.mint}`);
      }
      
      // If we're not in a widget, disconnect WebSocket when component unmounts
      // This prevents keeping connections open when not viewing the OrderBook
      if (!isWidget) {
        console.log('Disconnecting WebSocket service');
        wsService.disconnect();
      }
    };
  }, [effectiveToken, apiKeyMissing, fetchTrades, isWidget]);

  // Toggle between USD and SOL values
  const toggleValueDisplay = () => {
    setShowSolValues(!showSolValues);
  };

  // Format time since trade
  const getTimeSince = (timestamp: number): string => {
    const now = Date.now();
    const secondsAgo = Math.floor((now - timestamp) / 1000);
    
    if (secondsAgo < 60) return `${secondsAgo}s`;
    if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)}m`;
    if (secondsAgo < 86400) return `${Math.floor(secondsAgo / 3600)}h`;
    
    return `${Math.floor(secondsAgo / 86400)}d`;
  };

  // Live timer component for individual trades
  const LiveTimer: React.FC<{ timestamp: number }> = ({ timestamp }) => {
    const [timeString, setTimeString] = useState(getTimeSince(timestamp));

    useEffect(() => {
      const updateTime = () => {
        setTimeString(getTimeSince(timestamp));
      };

      // Update immediately
      updateTime();

      // Update every second
      const interval = setInterval(updateTime, 1000);

      return () => clearInterval(interval);
    }, [timestamp]);

    return <span>{timeString}</span>;
  };

  // Format number with commas
  const formatNumber = (num: number, decimals = 6): string => {
    const fixedNum = num.toFixed(decimals);
    const parts = fixedNum.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Format as USD
  const formatUSD = (value: number): string => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(value);
  };

  // Truncate wallet address
  const truncateAddress = (address: string): string => {
    return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
  };

  // Render API Key configuration message
  const renderApiKeyMessage = () => (
    <div className="flex flex-col items-center justify-center h-full p-3 text-center">
      <div className="bg-amber-800/20 p-3 rounded-lg border border-amber-800/30 mb-2 max-w-md">
        <div className="flex items-center justify-center mb-2">
          <AlertCircle size={16} className="text-amber-500 mr-1.5" />
          <h3 className="text-amber-500 font-medium text-xs">API Key Required</h3>
        </div>
        <p className="text-gray-300 text-[10px] mb-2">
          To use the Order Book widget, you need to set up your Solana Tracker API key.
        </p>
        <div className="bg-gray-900/50 p-2 rounded text-left">
          <p className="text-[9px] text-gray-400 mb-1">Add this to your <code className="bg-gray-800 px-1 py-0.5 rounded">.env</code> file:</p>
          <code className="text-[9px] block bg-gray-800 p-1.5 rounded overflow-x-auto whitespace-pre mb-1">
            VITE_SOLANA_TRACKER_API_KEY=your_api_key_here
          </code>
          <p className="text-[9px] text-gray-400">
            Get your API key from <a href="https://data.solanatracker.io/" target="_blank" rel="noopener noreferrer" className="text-[#00fe76] hover:underline">data.solanatracker.io</a>
          </p>
        </div>
      </div>
    </div>
  );

  // Update the renderWebSocketStatus function to use wsService status
  const renderWebSocketStatus = () => {
    if (wsStatus === 'connected') {
      return (
        <div className="flex items-center text-[9px] text-[#00fe76] gap-1 ml-1">
          <div className="w-1.5 h-1.5 bg-[#00fe76] rounded-full"></div>
          <span>Live</span>
        </div>
      );
    } else if (wsStatus === 'error' || wsStatus === 'disabled') {
      return (
        <div className="flex items-center text-[9px] text-gray-400 gap-1 ml-1">
          <div className="w-1.5 h-1.5 bg-gray-500 rounded-full"></div>
          <span>REST</span>
        </div>
      );
    }
    return null;
  };

  // Update the renderTradesList function
  const renderTradesList = () => {
    if (trades.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-4">
          <p className="text-gray-400 text-[10px]">
            {effectiveToken 
              ? `No trades found for ${effectiveToken.symbol || 'this token'}`
              : 'Select a token to view trades'}
          </p>
        </div>
      );
    }

    // Calculate the width percentage for volume bars
    const calculateVolumeBarWidth = (volume: number): number => {
      // Cap at 1000 for a full bar
      const maxVolume = 1000;
      // Calculate percentage (0-100)
      return Math.min(Math.max((volume / maxVolume) * 100, 5), 100); // Minimum 5% to ensure tiny trades are visible
    };

    return (
      <div className="overflow-x-auto hide-scrollbar">
        <table className="w-full text-xs">
          <thead className="bg-gray-100 uppercase border-b border-gray-200">
            <tr>
              <th className="px-1.5 py-1 text-left cursor-pointer" onClick={toggleValueDisplay}>
                <span className="text-[#24c35b] font-medium">Amount</span>
              </th>
              <th className="px-1.5 py-1 text-left">
                <span className="text-gray-600 font-medium">Trader</span>
              </th>
              <th className="px-1.5 py-1 text-right">
                <span className="text-gray-600 font-medium">Age</span>
              </th>
            </tr>
          </thead>
          <tbody className="text-black">
            {trades.map((trade, index) => (
              <tr 
                key={`${trade.tx}-${index}`} 
                className={`border-b border-gray-100 hover:bg-gray-50 ${trade.type === 'buy' ? 'hover:bg-green-50' : 'hover:bg-red-50'}`}
              >
                <td className="px-1.5 py-0.5 relative">
                  {/* Volume background bar */}
                  <div 
                    className={`absolute inset-y-0 left-0 ${trade.type === 'buy' ? 'volume-bar-buy' : 'volume-bar-sell'}`}
                    style={{ width: `${calculateVolumeBarWidth(trade.volume)}%` }}
                  ></div>
                  
                  {/* Amount text (on top of the bar) */}
                  <div 
                    className={`relative z-10 flex items-center ${trade.type === 'buy' ? 'text-[#00fe76]' : 'text-[#ff5c5c]'} cursor-pointer`}
                    onClick={toggleValueDisplay}
                  >
                    {showSolValues ? (
                      <div className="flex items-center">
                        <img src={SolanaLogoUrl} alt="Solana Logo" className="w-[8.4px] h-[8.4px] mr-1 -mt-[1px]" />
                        <span className="font-medium">{trade.volumeSol?.toFixed(4) || (trade.volume * 0.008).toFixed(4)}</span>
                      </div>
                    ) : (
                      <span className="font-medium">${Number(trade.volume).toFixed(2)}</span>
                    )}
                  </div>
                </td>
                <td className="px-1.5 py-0.5">
                  <div className="flex items-center">
                    <span className="text-xs text-black font-medium">{truncateAddress(trade.wallet)}</span>
                    <a
                      href={`https://solscan.io/account/${trade.wallet}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-1 text-[#00fe76] hover:text-[#00fe76]/80"
                    >
                      <ExternalLink size={8} />
                    </a>
                  </div>
                </td>
                <td className="px-1.5 py-0.5 text-right">
                  <div className="flex items-center justify-end text-[10px] text-gray-400">
                    <LiveTimer timestamp={trade.time} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Update the content section to add WebSocket status indicator
  const content = (
    <div className="flex flex-col h-full bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200">
      {/* Add style for hiding scrollbars and volume bars */}
      <style>
        {scrollbarHidingStyle}
        {volumeBarStyles}
      </style>
      
      {/* Header with homepage styling */}
      <div className="border-b border-gray-200 bg-white rounded-t-xl">
        {/* Title section with padding */}
        <div className="p-3 mb-1">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
              <h2 className="text-sm font-medium text-black">Order Book</h2>
              {effectiveToken && renderWebSocketStatus()}
            </div>
            <button
              onClick={() => effectiveToken?.mint && fetchTrades(effectiveToken.mint)}
              disabled={isLoading || !effectiveToken || apiKeyMissing}
              className="p-1.5 rounded-lg text-[#24c35b] hover:bg-[#24c35b]/10 disabled:opacity-50 disabled:cursor-not-allowed border border-[#24c35b]/20"
              title="Refresh"
            >
              <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" fill="none">
                <path d="M1 4v6h6" />
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Token Selection */}
        {effectiveToken ? (
          <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {effectiveToken.image_uri && (
                <img 
                  src={effectiveToken.image_uri} 
                  alt={effectiveToken.symbol || 'Token'} 
                  className="w-6 h-6 rounded-full object-cover border border-gray-200"
                />
              )}
              <div>
                <div className="flex items-center">
                  <span className="font-medium text-xs text-black">{effectiveToken.symbol}</span>
                  <span className="text-[9px] text-gray-600 ml-1">({(effectiveToken.name || 'Unknown Token').slice(0, 8)}{(effectiveToken.name || '').length > 8 ? '...' : ''})</span>
                </div>
                <div className="text-[9px] text-gray-500">
                  {effectiveToken.mint?.slice(0, 4)}...{effectiveToken.mint?.slice(-4)}
                </div>
              </div>
            </div>
            
            <button
              ref={setSearchButtonRef}
              onClick={() => setIsTokenSearchModalOpen(true)}
              className="px-3 py-1.5 rounded-lg bg-[#24c35b]/10 hover:bg-[#24c35b]/20 text-[#24c35b] transition-all text-xs border border-[#24c35b]/20"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="bg-gray-50 border-b border-gray-200">
            <button
              ref={setSearchButtonRef}
              onClick={() => setIsTokenSearchModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-black transition-all text-sm py-3 hover:bg-gray-100"
            >
              <Search size={14} />
              Select token
            </button>
          </div>
        )}
      </div>
      
      {/* Trades List */}
      <div className="flex-1 overflow-y-auto hide-scrollbar">
        {apiKeyMissing ? (
          renderApiKeyMessage()
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader size={16} className="text-[#00fe76] animate-spin mb-1" />
            <p className="text-gray-400 text-[10px]">Loading...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full p-3">
            <p className="text-red-400 text-[10px] mb-1.5">{error}</p>
            <button 
              onClick={() => effectiveToken?.mint && fetchTrades(effectiveToken.mint)}
              className="px-1.5 py-0.5 bg-[#00fe76]/20 text-[#00fe76] text-[10px] rounded-md hover:bg-[#00fe76]/30"
              disabled={apiKeyMissing}
            >
              Try Again
            </button>
          </div>
        ) : (
          renderTradesList()
        )}
      </div>
      
      {/* Token Search Modal */}
      <TokenSearchModal
        isOpen={isTokenSearchModalOpen}
        onClose={() => setIsTokenSearchModalOpen(false)}
        onSelectToken={(token: Token) => {
          setLocalSelectedToken(token);
          setIsTokenSearchModalOpen(false);
          // No need to call broadcastTokenSelection here as the useEffect will handle it
        }}
        referenceElement={searchButtonRef}
      />
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">
        {content}
      </div>
    );
  }

  // Otherwise, return the full component with container
  return (
    <div className="rounded-lg border border-[#24c35b]/40 overflow-hidden">
      {content}
    </div>
  );
};

export default OrderBook; 